/**
 * studio-shop D4, D5, D6, D8 — the shop's requests on the studio's website.
 *
 * The bag is priced by the server from the catalog; the quote and the order
 * are the server's totals. The card panel pays for a shop order exactly as it
 * pays for a booking; only its intent request differs. The server keeps one
 * chargeable payment per order, so the panel's per-mount key is not sent: the
 * order's route resumes the same payment on a reload or a second tab.
 *
 * Credentials (D8). The claim token, given only to the browser that placed the
 * order, pays for and links it; the read token opens its page (the thank-you
 * address and every email carry it as `k`). Both stay in this tab's
 * `sessionStorage`, and `k` leaves the address bar as soon as it is read, so
 * it never reaches a referrer, a screenshot or a shared link. The order's
 * signed-in owner needs neither.
 *
 * shop-self-fulfilment D5: the checkout's key stays in this tab beside the
 * order it made last; the bag price, the quote and order creation send that
 * order with its claim as `own`, so what it sets aside still counts as left
 * for this checkout after a reload or a changed address. The keys sent whose
 * answer never came go with it (`checkoutKeys`, code review Medium 2).
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  BagDto,
  BuyerOrderDto,
  CheckoutQuoteDto,
  CreatedOrderDto,
  LinkedOrderDto,
  SavedDetailsDto,
  SiteShopCatalogDto,
} from '@telify/shared/shop/site-dtos';
import type { CreatePaymentIntentResponse } from '@/api/types';
import { formatCurrency } from '@/components/payment/formatCurrency';
import type { PaymentIntentRequest } from '@/hooks/useCreatePaymentIntent';
import { formatDollarAmount } from '@/utils/formatPrice';

import type { BagLine } from '../hooks/useBag';
import { CLIENT_SCOPE, sitePost } from './session';
import { siteFetch } from './site';

export interface ShopAddress {
  readonly line1: string;
  readonly line2: string | null;
  readonly city: string;
  readonly state: string | null;
  readonly postalCode: string;
  readonly country: string;
}

export interface NewShopOrder {
  readonly checkoutKey: string;
  readonly email: string;
  /** The parcel's name. */
  readonly name: string;
  /** site-form-buy-mode (walk row 8): a guest's own name, apart from the parcel's, when the bag carries a form's answers. */
  readonly buyerName?: string;
  readonly address: ShopAddress;
  /** site-form-buy-mode D3: a line a website form claimed carries the key of the answers held for it. */
  readonly lines: readonly { optionId: string; quantity: number; answers?: string }[];
  /** D5: the buyer ticked that they are 18 or over and the order ships in an adult's name. */
  readonly adultConfirmed: true;
}

export const shopKeys = {
  catalog: ['web', 'shop', 'catalog'] as const,
  bag: (lines: readonly BagLine[]) => ['web', 'shop', 'bag', lines.map((l) => `${l.optionId}x${l.quantity}@${l.price ?? ''}`).join(',')] as const,
  /** The order reads under the client scope, so a sign-in or sign-out reads it again (an owner reads it without a token). */
  order: (orderId: string) => [...CLIENT_SCOPE, 'shop-order', orderId] as const,
  savedDetails: [...CLIENT_SCOPE, 'shop-saved-details'] as const,
};

/**
 * D3: the products the studio shows, their options on sale, the shop's currency and its free-shipping countries.
 * shop-self-fulfilment D5 (code review Medium 4): read again every minute while the page is in view, so Sold out turns.
 */
export function useShopCatalog(enabled = true) {
  return useQuery<SiteShopCatalogDto>({
    queryKey: shopKeys.catalog,
    queryFn: () => siteFetch<SiteShopCatalogDto>('/shop/products'),
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** D4: the bag priced from the catalog; lines no longer sold come back marked. A change of quantity keeps the last answer on screen. */
export function useBagPrice(lines: readonly BagLine[]) {
  return useQuery<BagDto>({
    queryKey: shopKeys.bag(lines),
    // Each line's last shown price marks a change; a claimed line's answers are not the route's to read.
    queryFn: () => sitePost<BagDto>('/shop/bag/price', { lines: lines.map(({ answers: _answers, ...line }) => line), ...ownCheckout() }),
    enabled: lines.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** D8: a signed-in buyer's email and address from their newest paid order here; null when none. Never cached. */
export function useSavedDetails(signedIn: boolean) {
  return useQuery<SavedDetailsDto | null>({
    queryKey: shopKeys.savedDetails,
    queryFn: async () => (await siteFetch<{ details: SavedDetailsDto | null }>('/shop/checkout/details')).details,
    enabled: signedIn,
    staleTime: Infinity,
    retry: false,
  });
}

/** Countries whose full names read with "the" (design thread, 0eb92339d: full names, never "US"). */
const COUNTRY_WORDS: Record<string, string> = {
  US: 'the United States',
  GB: 'the United Kingdom',
  NL: 'the Netherlands',
  PH: 'the Philippines',
  AE: 'the United Arab Emirates',
};

/** D4: the shipping line, from the countries the studio ships to free. */
export function shippingLine(freeShippingCountries: readonly string[]): string {
  if (freeShippingCountries.length === 0) return 'Shipping and tax are worked out at checkout.';
  let regions: Intl.DisplayNames | null = null;
  try {
    regions = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    /* no region names in this browser: the codes stand */
  }
  const names = freeShippingCountries.map((code) => COUNTRY_WORDS[code] ?? regions?.of(code) ?? code);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Free shipping in ${list}. Other countries see their shipping at checkout.`;
}

/**
 * D5: a delivery estimate's calendar dates (`2026-11-02`, `2026-11-06`) as drawn after "Arrives": "Nov 2 to 6", or
 * "Oct 30 to Nov 4" across months; one day alone when they are the same.
 */
export function arrivalDates(from: string, to: string): string {
  const parts = (day: string) => {
    const date = new Date(`${day}T00:00:00Z`);
    return {
      month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(date),
      day: date.getUTCDate(),
      monthIndex: date.getUTCMonth(),
    };
  };
  const a = parts(from);
  const b = parts(to);
  if (from === to) return `${a.month} ${a.day}`;
  return a.monthIndex === b.monthIndex ? `${a.month} ${a.day} to ${b.day}` : `${a.month} ${a.day} to ${b.month} ${b.day}`;
}

/** Money math in the shop's currency (`'95.00'`): the subtotal, shipping, tax, total and what was paid, "$95.00". */
export function shopMoney(amount: string, currency: string): string {
  return formatCurrency(Number(amount), currency);
}

/**
 * An item's price as the grid, the window and an order's lines draw it: "$168", cents only when it has any
 * (`formatPrice`'s rule, as the website's other prices; look check item 5). Another currency than the dollar keeps
 * its cents.
 */
export function shopPrice(amount: string, currency: string): string {
  return currency.toUpperCase() === 'USD' ? formatDollarAmount(Number(amount)) : shopMoney(amount, currency);
}

/** D5: the server's shipping, tax and total for the bag and the address. */
export function quoteShop(lines: readonly { optionId: string; quantity: number }[], address: ShopAddress): Promise<CheckoutQuoteDto> {
  return sitePost<CheckoutQuoteDto>('/shop/quote', { lines, address, ...ownCheckout() });
}

/** D5: the unpaid order; the same checkout key returns the same order with fresh credentials. */
export function createShopOrder(order: NewShopOrder): Promise<CreatedOrderDto> {
  return sitePost<CreatedOrderDto>('/shop/orders', { ...order, ...ownCheckout() });
}

/** D8: the order for its read token, or for its signed-in owner. Right after paying it is read again every two seconds until the payment is recorded. */
export function useShopOrder(
  orderId: string,
  readToken: string | null,
  { confirming = false, enabled = true }: { confirming?: boolean; enabled?: boolean } = {},
) {
  return useQuery<BuyerOrderDto>({
    queryKey: shopKeys.order(orderId),
    queryFn: async () => {
      const query = readToken ? `?${new URLSearchParams({ k: readToken })}` : '';
      return (await siteFetch<{ order: BuyerOrderDto }>(`/shop/orders/${encodeURIComponent(orderId)}${query}`)).order;
    },
    enabled,
    retry: false,
    refetchInterval: (q) => (confirming && q.state.data?.payment === 'awaiting_payment' ? 2000 : false),
  });
}

/** D8: links the order to the signed-in client, with this browser's claim. */
export function linkShopOrder(orderId: string, claimToken: string): Promise<LinkedOrderDto> {
  return sitePost<LinkedOrderDto>(`/shop/orders/${encodeURIComponent(orderId)}/link`, { claimToken });
}

/** The shop order's payment intent request for the card panel. */
export function shopOrderIntentRequest(orderId: string, claimToken: string | null): PaymentIntentRequest {
  return (body) =>
    sitePost<CreatePaymentIntentResponse>(`/shop/orders/${encodeURIComponent(orderId)}/intent`, {
      ...body,
      ...(claimToken ? { claimToken } : {}),
    });
}

// ---------------------------------------------------------------- credentials in this tab

const claimKey = (orderId: string) => `telify:shop-claim:${orderId}`;
const readKey = (orderId: string) => `telify:shop-read:${orderId}`;

function stored(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the page holds them until it closes */
  }
}

/** D8: keeps a new order's credentials in this tab only. */
export function keepOrderCredentials(orderId: string, credentials: { readToken: string; claimToken: string }): void {
  store(readKey(orderId), credentials.readToken);
  store(claimKey(orderId), credentials.claimToken);
}

export function orderClaim(orderId: string): string | null {
  return stored(claimKey(orderId));
}

/** shop-self-fulfilment D5: the read token this tab keeps for an order it made. */
export function orderReadToken(orderId: string): string | null {
  return stored(readKey(orderId));
}

const CHECKOUT_KEY = 'telify:shop-checkout-key';

/** D5: the checkout key kept in this tab and the content it was made for; shop-self-fulfilment D5: the order it made last. */
export interface KeptCheckout {
  readonly key: string;
  readonly fingerprint: string;
  readonly orderId?: string;
  /** shop-self-fulfilment D5: the keys sent, the last three, until an answer names the order (code review Medium 2). */
  readonly sent?: readonly string[];
}

export function keptCheckout(): KeptCheckout | null {
  try {
    return JSON.parse(stored(CHECKOUT_KEY) ?? 'null') as KeptCheckout | null;
  } catch {
    return null;
  }
}

export function keepCheckout(kept: KeptCheckout): void {
  store(CHECKOUT_KEY, JSON.stringify(kept));
}

/** shop-self-fulfilment D5: a key about to be sent, kept with the last three, so an order whose answer is lost is still found. */
export function keepSent(key: string): void {
  const kept = keptCheckout();
  if (kept) keepCheckout({ ...kept, sent: [...(kept.sent ?? []).filter((k) => k !== key), key].slice(-3) });
}

/** shop-self-fulfilment D5: the checkout's order is paid: the next checkout in this tab starts new. */
export function forgetCheckout(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_KEY);
  } catch {
    /* storage unavailable: nothing kept */
  }
}

/**
 * shop-self-fulfilment D5: the checkout's own order with its claim, and the keys sent whose answer never came, read
 * at each request; nothing when this tab has neither. site-form-buy-mode: the form's hold sends it too.
 */
export function ownCheckout(): { own?: { orderId?: string; claimToken?: string; checkoutKeys?: string[] } } {
  const kept = keptCheckout();
  const orderId = kept?.orderId;
  const claimToken = orderId ? orderClaim(orderId) : null;
  const claimed = orderId && claimToken ? { orderId, claimToken } : null;
  const sent = kept?.sent?.length ? { checkoutKeys: [...kept.sent] } : null;
  return claimed || sent ? { own: { ...claimed, ...sent } } : {};
}

/**
 * D8: the order's read token. A `k` in the address is kept in this tab and
 * taken out of the address bar at once; otherwise the one kept earlier.
 */
export function takeReadToken(orderId: string): string | null {
  const url = new URL(window.location.href);
  const k = url.searchParams.get('k');
  if (k) {
    store(readKey(orderId), k);
    url.searchParams.delete('k');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    return k;
  }
  return stored(readKey(orderId));
}
